import stats from "./content/site-stats.json";
import { CTABand } from "../components/Buttons";
import { useMeta } from "../hooks/useMeta";
import { Shell, H2, Reveal, MiniCaseCard, Steps } from "./parts";
import { WinsTeaser } from "./WinsTeaser";

const DIFFERENT = [
  {
    title: "We manage to the number that matters.",
    body: "Every service has one number an owner actually cares about, and that is what we report. Paid traffic is managed to the profit of the whole business, not platform ROAS. Retention is managed to revenue per recipient and share of revenue, not open rates. Creative is scored by how much of the account's spend it earns. AEO is measured by share of answers. Vanity metrics are on the report, but never the headline.",
    proof: [
      { value: "12%", label: "net profit, from break-even", client: "Clean-Eez, paid traffic" },
      { value: "56.58%", label: "of revenue from email and SMS", client: "Jamie Anne, retention" },
    ],
  },
  {
    title: "We make the work ourselves.",
    body: "Creative is the biggest lever on paid social and the one most agencies outsource. Ours is in-house: strategists, designers, editors and sourced UGC creators shipping into the account every week. Same for email: every send is designed to the brand's standard by our team. For Good Riddance that was 118 concepts and 743 variations in 14 months, and the Paid Performance Specialists moved 76% of the budget onto it because it out-earned the alternative.",
    images: ["/assets/good-riddance/gr61.webp", "/assets/good-riddance/gr22.webp", "/assets/jamie-anne/bare-minimum.webp", "/assets/smart-earth/barn-program.webp"],
  },
  {
    title: "We're in the account every day.",
    body: "No account managers relaying messages. The people who run your ads, creative and email are in one Slack channel with you. July 2026 at Clean-Eez: one creative format broke through, we scaled it and iterated the same week, and Meta went from $226k at 1.31 to $376k at 1.41 in a month. At Smart Earth, cutting summer send volume and cleaning 2,000+ dead leads per market lifted sender scores to 76 and 70 before Q4.",
    proof: [
      { value: "$226k to $376k", label: "Meta spend in one month, at a higher ROAS", client: "Clean-Eez, July 2026" },
      { value: "76 / 70", label: "sender scores, Canada and US, after the cuts", client: "Smart Earth" },
    ],
  },
  {
    title: "We've done this with our own money.",
    body: "We've built brands to $200 million, and we still run our own. When we tell you to cut spend, or to send less email, it's because we've had to do it ourselves. And when a client outgrows us, that's the goal: Good Riddance offboarded after 14 months because they scaled far enough to take creative in-house.",
    proof: [
      { value: "$200 million", label: "generated for our own brands", client: "Smart Marketer" },
      { value: "14 months", label: "then the client took creative in-house. That is the goal.", client: "Good Riddance" },
    ],
  },
];

const STEPS = [
  "Fit check. Tell us about your brand. If it's a fit, you book a call with Pep straight away.",
  "Discovery call. Where you are, what the main issues are, and whether a 90-day plan makes sense for both of us.",
  "The 90-day plan. On the second call we present the plan and the proposal: targets, channels, team, what happens in which week.",
  "Go live. From signed proposal to takeover in 10 business days: access, onboarding, one Slack channel, then we run it.",
];


function WeeklyReport() {
  return (
    <Shell testId="weekly-report">
      <H2>What you get every week.</H2>
      <p className="mt-3 max-w-2xl text-base text-navy/70 md:text-lg">One report. Spend, revenue, contribution margin, NCROAS and the creative scoreboard. [PLACEHOLDER: real report screenshot, anonymized]</p>
      <Reveal className="mt-10 overflow-hidden rounded-xl border border-mist">
        <div className="flex items-center justify-between bg-navy px-6 py-4 text-white">
          <span className="text-sm font-bold">Weekly profit review</span>
          <span className="text-xs uppercase tracking-widest text-white/60">Week 34, illustrative</span>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-fog text-xs uppercase tracking-widest text-navy/60">
            <tr><th className="px-6 py-3">Metric</th><th className="px-6 py-3">This week</th><th className="px-6 py-3">Last week</th><th className="px-6 py-3">Change</th></tr>
          </thead>
          <tbody>
            {stats.reportMock.map((r) => (
              <tr key={r[0]} className="border-t border-mist">
                <td className="px-6 py-3 font-bold text-navy">{r[0]}</td>
                <td className="px-6 py-3 text-navy">{r[1]}</td>
                <td className="px-6 py-3 text-navy/60">{r[2]}</td>
                <td className={`px-6 py-3 font-bold ${r[3].startsWith("+") ? "text-green" : "text-navy/60"}`}>{r[3]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Reveal>
    </Shell>
  );
}

export default function HowWeWork() {
  useMeta({ title: "How we work | Smart Marketer Agency", description: "One team on paid traffic, creative, retention and AEO. Run like it's our own brand." });
  return (
    <main data-testid="draft-how-we-work">
      <section className="bg-navy">
        <div className="mx-auto max-w-6xl px-5 py-20 md:py-28">
          <h1 className="max-w-3xl text-4xl font-bold leading-[1.08] text-white sm:text-5xl lg:text-[56px]">Run like it's our own brand.</h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">Most brands run four vendors who each optimize their own dashboard. We put one team on paid traffic, creative, retention and AEO, and we work closely with your team to give you the strategy and support that drives consistent, long-term growth.</p>
        </div>
      </section>

      <Shell testId="four-things">
        <H2>Four things we do differently.</H2>
        <div className="mt-12 space-y-16">
          {DIFFERENT.map((d, i) => (
            <Reveal key={d.title} className={`grid items-center gap-10 md:grid-cols-2 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">0{i + 1}</div>
                <h3 className="mt-3 text-2xl font-bold text-navy md:text-3xl">{d.title}</h3>
                <p className="mt-4 text-base leading-relaxed text-navy/80">{d.body}</p>
              </div>
              {d.images ? (
                <div className="grid grid-cols-2 gap-3">
                  {d.images.map((src) => (
                    <div key={src} className="aspect-[4/5] overflow-hidden rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)]">
                      <img src={src} alt="" loading="lazy" className="h-full w-full object-cover object-top" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid gap-4">
                  {d.proof.map((p) => (
                    <div key={p.value} className="rounded-xl bg-navy p-7">
                      <div className="text-4xl font-extrabold leading-none text-white md:text-5xl">{p.value}</div>
                      <div className="mt-3 text-sm text-white/75">{p.label}</div>
                      <div className="mt-1 text-xs font-bold uppercase tracking-widest text-pink">{p.client}</div>
                    </div>
                  ))}
                </div>
              )}
            </Reveal>
          ))}
        </div>
      </Shell>

      <Steps lead="From first hello to go-live." steps={STEPS} />

      <WeeklyReport />

      <Shell tone="navy" testId="hands-on">
        <H2 light>A close, hands-on relationship.</H2>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/75 md:text-lg">{stats.whoFor.intro}</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {["Access to the numbers", "A decision-maker in the channel", "A weekly 30 minutes"].map((t, i) => (
            <div key={t} className="rounded-xl bg-white/5 p-6">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">We need {i + 1}</div>
              <div className="mt-2 text-lg font-bold text-white">{t}</div>
            </div>
          ))}
        </div>
      </Shell>

      <Shell tone="fog" testId="hww-results">
        <H2>Results.</H2>
        <div className="mt-10 grid gap-6 lg:grid-cols-[1.25fr_1fr]">
          <WinsTeaser exclude={["clean-eez", "good-riddance", "jamie-anne"]} />
          <div className="grid gap-6">
            {["clean-eez", "good-riddance", "jamie-anne"].map((s) => <MiniCaseCard key={s} slug={s} />)}
          </div>
        </div>
      </Shell>

      <CTABand title="See if we're a fit." sub="Work with our Agency to get a proven approach tailored to your needs and executed by our results-driven team." />
    </main>
  );
}
